import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import { Card, Button, Typography } from "antd"
import { Icon } from "ui/Icon"
import CommonIcon from "components/common/Icon"
import CircleImage from "components/common/CircleImage"
import { cleanedExperiences, cleanExperiencesByGuide, resetBookingSelections } from "store/experiences"
import styles from "./ExperienceCard.module.css"
import { routes } from "router"
import { oldRoutes } from "router/old-routes"
import { numberToCurrency } from "helpers/string"
import { ShareAltOutlined, HeartFilled, HeartOutlined } from "@ant-design/icons"
import { ShareListingModal } from "pagesV2/pro/Experiences/Home/ShareModal"
import PricingDetailsTooltip from "ui/PricingDetailsTooltip"
import { isEmbeddedContext } from "helpers/embedded"

const { Title, Text } = Typography

// TODO: static rating until the guide rating is wired up (matches the trip card and detail page)
const GUIDE_RATING_STARS = 5

const IconButtons = ({ loggedIn, isFavorite, toggleFavorite, handleOpen }) => {
  return (
    <div className={styles.iconButtons}>
      <Button
        shape="circle"
        icon={<ShareAltOutlined style={{ color: "var(--color-primary)", fontSize: 20 }} />}
        className={styles.iconButton}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          handleOpen()
        }}
      />

      {loggedIn && (
        <Button
          shape="circle"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            toggleFavorite()
          }}
          icon={
            isFavorite ? (
              <HeartFilled style={{ color: "var(--color-primary)", fontSize: 20 }} />
            ) : (
              <HeartOutlined style={{ color: "var(--color-primary)", fontSize: 20 }} />
            )
          }
          className={styles.iconButton}
        />
      )}
    </div>
  )
}

const EmbeddedExperienceCard = ({
  favorite,
  showFavoriteButton,
  image,
  title,
  location,
  time,
  people,
  price,
  experienceId,
  mode,
  guuid,
  tripPrices,
  durationType,
  durationValue,
  guide
}) => {
  const guideName = guide?.company_name || guide?.display_name
  const { uuid } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  // Embedded (third-party widget iframe) must always behave as logged-out — no favorites UI —
  // even when a logged-in session exists in the same browser's shared localStorage.
  const loggedIn = isEmbeddedContext() ? null : localStorage.getItem("access_token")
  const userRole = useSelector((s) => s.user.role)
  const [isFavorite, setIsFavorite] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const url = `${global.location.origin}/#/trips/${experienceId}`

  useEffect(() => {
    setIsFavorite(favorite)
  }, [favorite])

  const handleOpen = () => {
    setIsShareModalOpen(true)
  }

  const handleClose = () => {
    setIsShareModalOpen(false)
  }

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite)
  }
  const handleCardClick = () => {
    if (mode) {
      navigate(routes.tripPath({ id: experienceId }))
    } else {
      dispatch(cleanedExperiences())
      navigate(routes?.embeddedExperienceDetail({ uuid: uuid, id: experienceId }))
    }
  }
  const handleBookClick = (e) => {
    e.stopPropagation()
    dispatch(resetBookingSelections())

    if (mode) {
      if (mode === "guideProfile") {
        navigate(routes.guideProfileExperiencesBookingPath({ uuid: guuid, id: experienceId }))
      } else if (loggedIn && userRole === "user") {
        navigate(routes.clientBookingWizardRootPath({ id: experienceId }))
      } else {
        navigate({ pathname: oldRoutes.bookingWizardPath({ id: experienceId }) })
      }
    } else {
      dispatch(cleanedExperiences())
      navigate(routes.embeddedExperienceDetail({ uuid, id: experienceId }))
    }

    dispatch(cleanExperiencesByGuide())
  }

  return (
    <>
      <Card
        cover={
          <div className={styles.coverImageContainer} onClick={handleCardClick} style={{ cursor: "pointer" }}>
            <img src={image} alt={title} className={styles.coverImage} />
          </div>
        }
        className={styles.card}
      >
        {showFavoriteButton && (
          <IconButtons loggedIn={loggedIn} isFavorite={isFavorite} toggleFavorite={toggleFavorite} handleOpen={handleOpen} />
        )}
        <Title level={5} className={styles.title} onClick={handleCardClick} style={{ cursor: "pointer" }}>
          {title}
        </Title>
        <div className={styles.infoRow}>
          <div className={styles.icon}>
            <Icon type="feather" name="Clock" width={16} height={16} />
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {time}
          </Text>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.icon}>
            <Icon type="feather" name="User" width={16} height={16} />
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {people} people
          </Text>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.icon}>
            <Icon type="feather" name="MapPin" width={16} height={16} />
          </div>
          <Text type="secondary" style={{ fontSize: 12 }} className={styles.locationText}>
            {location}
          </Text>
        </div>
        {(guideName || guide?.logo) && (
          <div className={styles.guideRow}>
            <CircleImage
              src={guide?.logo}
              alt={guideName || "Guide"}
              size={28}
              placeholderIconName="MyProfile"
              className="flex-shrink-0"
              border={false}
            />
            <span className={styles.guideName}>{guideName}</span>
            <div className={styles.stars}>
              {new Array(GUIDE_RATING_STARS).fill(null).map((_, index) => (
                <CommonIcon key={index} iconName="StarNew" className={styles.starFilled} size={14} block />
              ))}
            </div>
          </div>
        )}
        <div className={styles.footerRow}>
          <div className={styles.priceBlock}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              from{" "}
            </Text>
            <Text strong className={styles.price}>
              {numberToCurrency(price)}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {" "}
              / person
            </Text>
            <PricingDetailsTooltip tripPrices={tripPrices} durationType={durationType} durationValue={durationValue} />
          </div>
          <Button type="primary" className={styles.bookButton} onClick={handleBookClick}>
            Book Now
          </Button>
        </div>
      </Card>
      <ShareListingModal
        visible={isShareModalOpen}
        onClose={handleClose}
        listingTitle={title}
        listingAddress={location}
        listingImage={image}
        listingLink={url}
      />
    </>
  )
}

export default EmbeddedExperienceCard

IconButtons.propTypes = {
  loggedIn: PropTypes.bool.isRequired,
  isFavorite: PropTypes.bool.isRequired,
  toggleFavorite: PropTypes.func.isRequired,
  handleOpen: PropTypes.func.isRequired
}

EmbeddedExperienceCard.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  time: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string
  }),
  people: PropTypes.number,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  experienceId: PropTypes.string.isRequired,
  mode: PropTypes.string,
  favorite: PropTypes.bool,
  showFavoriteButton: PropTypes.bool,
  guuid: PropTypes.string,
  tripPrices: PropTypes.array,
  durationType: PropTypes.string,
  durationValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  guide: PropTypes.shape({
    company_name: PropTypes.string,
    display_name: PropTypes.string,
    logo: PropTypes.string,
    rating: PropTypes.number
  })
}
